import { SearchInput, type SearchOption } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { useSearchDraft } from "./useSearchDraft";
import styles from "./FilterSearchInput.module.css";

export type FilterSearchInputProps = {
  // 커밋된 검색어다(URL 이 소유한다). 초안은 이 컴포넌트 안에서 보관한다.
  value: string;
  onCommit: (value: string) => void;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  // 타이핑에서 커밋까지의 지연이다. 기본 300ms 다. Enter·✕ 는 즉시 커밋한다.
  debounceMs?: number;
  // 같은 축의 값 후보(자동완성 힌트)다. 고르면 그 값으로 **즉시 커밋**한다. 조건을 하나 더
  // 추가하지 않고 이 축의 값을 채운다. 그래서 후보가 곧 조건이 되는
  // `FilterSearchCombobox`(라벨 조건)와 용도가 다르다.
  options?: readonly SearchOption[];
  // 맞는 후보가 없을 때의 문구다. 후보를 전달할 때만 의미가 있다.
  emptyMessage?: string;
};

/**
 * 필터 바 행 1 의 이름 검색 입력이다. 후보 드롭다운이 없는 화면(Embodiments·Members)에서 쓴다.
 *
 * 크롬은 c6 `SearchInput` plain 모드(돋보기·✕ 오른쪽 안쪽, size 미지정 = md, rules.md §17)를
 * 그대로 쓴다. 여기서는 초안과 커밋의 분리(`useSearchDraft`)만 추가한다. 후보를 보여 주는 화면은
 * 같은 훅으로 `FilterSearchCombobox` 를 만든다. 폭은 부모(`FilterBar` 의 검색 슬롯)가 정한다.
 */
export function FilterSearchInput({
  value,
  onCommit,
  ariaLabel,
  placeholder,
  className,
  disabled = false,
  debounceMs,
  options,
  emptyMessage,
}: FilterSearchInputProps): ReactNode {
  const { draft, change, commitNow } = useSearchDraft(value, onCommit, debounceMs);
  return (
    <div role="search" className={className ? `${styles.root} ${className}` : styles.root}>
      <SearchInput
        value={draft}
        onValueChange={change}
        onSearch={commitNow}
        options={options}
        emptyMessage={emptyMessage}
        showClear
        disabled={disabled}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
    </div>
  );
}
